/**
 * AdminProfileReviewQueue — held entity profiles triage.
 *
 * Shows every profile in `needs_review` with its failed validation gate,
 * dead source URLs, dropped claims and one-click actions:
 *   Replace source (live-validated before applied) · Re-run gates (no LLM).
 * Evidence floors are never altered from this panel.
 */
import React, { useCallback, useEffect, useState } from "react";
import {
  RefreshCw, Loader2, ExternalLink, Replace, ShieldAlert,
  CheckCircle2, FileWarning, UserSearch, ImageOff,
} from "lucide-react";
import { toast } from "sonner";
import {
  adminProfileReviewQueue, adminProfileReviewReplaceSource,
  adminProfileReviewRevalidate,
} from "../../lib/api";

const GATE_LABEL = {
  source_floor: "SOURCE FLOOR",
  documentation_score: "DOC SCORE",
  claims_lost_citations: "CLAIMS LOST CITATIONS",
  identity_ambiguous: "IDENTITY AMBIGUOUS",
  other: "OTHER",
};

const _fmt = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
};

const btn = "inline-flex items-center gap-1 px-2 py-1 border font-mono text-[8px] uppercase tracking-[0.18em] transition-colors disabled:opacity-40";

const DeadSourceRow = ({ item, dead, token, userToken, onChanged }) => {
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState("");
  const [title, setTitle] = useState("");
  const [busy, setBusy] = useState(false);

  const apply = async () => {
    if (!url.trim()) return;
    setBusy(true);
    try {
      const res = await adminProfileReviewReplaceSource(token, {
        slug: item.slug, index: dead.index, url: url.trim(),
        title: title.trim() || undefined,
      }, userToken);
      toast.success(`Source ${dead.index} replaced (HTTP ${res.http_status}) — re-run gates to apply`);
      setOpen(false);
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Replacement failed validation");
    } finally { setBusy(false); }
  };

  return (
    <div className="border border-[#3a2523] bg-[#171210] p-2 mt-1.5"
         data-testid={`dead-source-${item.slug}-${dead.index}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-mono text-[8px] uppercase tracking-[0.2em] px-1.5 py-0.5 bg-[#D92D20] text-[#0a0907]">
          DEAD #{dead.index}
        </span>
        <span className="font-mono text-[9px] text-[#A39E93] break-all flex-1">{dead.url}</span>
        <a href={dead.url} target="_blank" rel="noreferrer"
           className={`${btn} border-[#454038] text-[#7B7168] hover:border-[#58A6FF] hover:text-[#58A6FF]`}
           data-testid={`dead-source-open-${item.slug}-${dead.index}`}>
          <ExternalLink size={9} /> Open
        </a>
        <button type="button" onClick={() => setOpen((v) => !v)}
                className={`${btn} border-[#3FB950] text-[#3FB950] hover:bg-[#3FB950] hover:text-[#0a0907]`}
                data-testid={`dead-source-replace-btn-${item.slug}-${dead.index}`}>
          <Replace size={9} /> Replace
        </button>
      </div>
      {dead.title && <div className="mt-1 font-mono text-[9px] text-[#7B7168]">{dead.title}</div>}
      {open && (
        <div className="mt-2 space-y-1.5" data-testid={`replace-form-${item.slug}-${dead.index}`}>
          <input value={url} onChange={(e) => setUrl(e.target.value)}
                 placeholder="Authentic replacement URL (validated live before applying)"
                 data-testid={`replace-url-input-${item.slug}-${dead.index}`}
                 className="w-full bg-[#0f0d0b] border border-[#2D2A26] px-2 py-1.5 font-mono text-[10px] text-[#E4DFD3] focus:border-[#3FB950] outline-none" />
          <input value={title} onChange={(e) => setTitle(e.target.value)}
                 placeholder="Title (optional — keeps original if blank)"
                 data-testid={`replace-title-input-${item.slug}-${dead.index}`}
                 className="w-full bg-[#0f0d0b] border border-[#2D2A26] px-2 py-1.5 font-mono text-[10px] text-[#E4DFD3] focus:border-[#3FB950] outline-none" />
          <button type="button" disabled={busy || !url.trim()} onClick={apply}
                  data-testid={`replace-apply-btn-${item.slug}-${dead.index}`}
                  className={`${btn} border-[#3FB950] text-[#3FB950] hover:bg-[#3FB950] hover:text-[#0a0907]`}>
            {busy ? <Loader2 size={9} className="animate-spin" /> : <CheckCircle2 size={9} />}
            Validate &amp; apply
          </button>
        </div>
      )}
    </div>
  );
};

const HeldProfileCard = ({ item, token, userToken, onChanged }) => {
  const [busy, setBusy] = useState(false);

  const revalidate = async () => {
    setBusy(true);
    try {
      const res = await adminProfileReviewRevalidate(token, item.slug, userToken);
      if (res.status === "published") {
        toast.success(`${item.slug} PUBLISHED — ${res.validated_sources} sources, ${res.claims} claims`);
      } else {
        toast.warning(`${item.slug} still held: ${res.reason || res.status}`);
      }
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Revalidation failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="border border-[#2D2A26] bg-[#12110e] p-4"
         data-testid={`review-queue-item-${item.slug}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-mono text-[8px] uppercase tracking-[0.25em] px-1.5 py-0.5 bg-[#D9A56C] text-[#0a0907]"
              data-testid={`failed-gate-${item.slug}`}>
          {GATE_LABEL[item.failed_gate] || item.failed_gate}
        </span>
        <span className="font-display text-lg text-[#E4DFD3]">{item.name}</span>
        <span className="font-mono text-[9px] text-[#7B7168]">{item.kind}</span>
        <span className="font-mono text-[9px] text-[#7B7168] ml-auto">held {_fmt(item.held_at)}</span>
      </div>

      <div className="mt-1.5 font-mono text-[10px] text-[#C9C2B4]" data-testid={`hold-reason-${item.slug}`}>
        <ShieldAlert size={10} className="inline mr-1 text-[#D9A56C]" />
        {item.reason}
      </div>

      <div className="mt-2 flex flex-wrap gap-3 font-mono text-[9px] text-[#A39E93]">
        <span data-testid={`sources-count-${item.slug}`}>
          sources <b className={item.validated_sources < item.required_sources ? "text-[#D92D20]" : "text-[#3FB950]"}>
            {item.validated_sources}/{item.required_sources}
          </b>
        </span>
        <span>doc score <b className={item.documentation_score < item.min_doc_score ? "text-[#D92D20]" : "text-[#3FB950]"}>
          {item.documentation_score}
        </b> (floor {item.min_doc_score})</span>
        <span>claims <b className="text-[#E4DFD3]">{item.claims}</b></span>
        {item.portrait_missing && (
          <span className="text-[#E0A526]"><ImageOff size={9} className="inline mr-0.5" />no licensed portrait</span>
        )}
      </div>

      {item.dead_sources.length > 0 && (
        <div className="mt-3">
          <div className="font-mono text-[8px] uppercase tracking-[0.25em] text-[#7B7168]">
            Dead / rejected source URLs ({item.dead_sources.length})
          </div>
          {item.dead_sources.map((d) => (
            <DeadSourceRow key={d.index} item={item} dead={d}
                           token={token} userToken={userToken} onChanged={onChanged} />
          ))}
        </div>
      )}

      {item.claims_dropped.length > 0 && (
        <div className="mt-3" data-testid={`dropped-claims-${item.slug}`}>
          <div className="font-mono text-[8px] uppercase tracking-[0.25em] text-[#7B7168]">
            Dropped claims ({item.claims_dropped.length})
          </div>
          {item.claims_dropped.map((c, i) => (
            <div key={i} className="mt-1 font-mono text-[9px] text-[#A39E93]">
              <FileWarning size={9} className="inline mr-1 text-[#D9A56C]" />
              {c.statement}… <span className="text-[#7B7168]">[{c.because}]</span>
            </div>
          ))}
        </div>
      )}

      <div className="mt-3 flex flex-wrap gap-1.5">
        <button type="button" disabled={busy || !item.has_cache} onClick={revalidate}
                data-testid={`revalidate-btn-${item.slug}`}
                className={`${btn} border-[#58A6FF] text-[#58A6FF] hover:bg-[#58A6FF] hover:text-[#0a0907]`}>
          {busy ? <Loader2 size={9} className="animate-spin" /> : <RefreshCw size={9} />}
          Re-run gates (no LLM)
        </button>
        <a href={`/entity/${item.kind}/${item.slug}`} target="_blank" rel="noreferrer"
           data-testid={`preview-profile-btn-${item.slug}`}
           className={`${btn} border-[#454038] text-[#A39E93] hover:border-[#E4DFD3] hover:text-[#E4DFD3]`}>
          <ExternalLink size={9} /> Preview profile
        </a>
        {!item.has_cache && (
          <span className="font-mono text-[8px] text-[#D92D20] self-center">
            no stage cache — needs full regeneration
          </span>
        )}
      </div>
    </div>
  );
};

export const AdminProfileReviewQueue = ({ token, userToken }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await adminProfileReviewQueue(token, userToken));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load review queue");
    } finally { setLoading(false); }
  }, [token, userToken]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="max-w-[1100px] mx-auto px-4 md:px-8 py-8" data-testid="profile-review-queue-panel">
      <div className="flex flex-wrap items-center gap-3">
        <UserSearch size={16} className="text-[#D9A56C]" />
        <h2 className="font-display text-2xl text-[#E4DFD3]">Profile Review Queue</h2>
        <span className="font-mono text-[9px] text-[#7B7168]" data-testid="review-queue-count">
          {data ? `${data.count} held` : "…"}
        </span>
        <button type="button" onClick={load} disabled={loading}
                data-testid="review-queue-refresh-btn"
                className={`${btn} ml-auto border-[#454038] text-[#A39E93] hover:border-[#E4DFD3] hover:text-[#E4DFD3]`}>
          {loading ? <Loader2 size={9} className="animate-spin" /> : <RefreshCw size={9} />} Refresh
        </button>
      </div>
      <p className="mt-1 font-mono text-[10px] text-[#7B7168]">
        Profiles held by the Entity Research Standard gates. Replacements are HTTP-validated
        before they are applied; re-running gates never calls the LLM and never lowers the floors.
      </p>

      <div className="mt-5 space-y-4">
        {loading && !data && (
          <div className="font-mono text-[10px] text-[#7B7168]">Loading…</div>
        )}
        {data && data.count === 0 && (
          <div className="border border-[#2D2A26] p-6 text-center" data-testid="review-queue-empty">
            <CheckCircle2 size={18} className="inline text-[#3FB950]" />
            <div className="mt-2 font-mono text-[10px] text-[#A39E93]">
              Review queue clear — no profiles held.
            </div>
          </div>
        )}
        {data && data.items.map((item) => (
          <HeldProfileCard key={item.slug} item={item}
                           token={token} userToken={userToken} onChanged={load} />
        ))}
      </div>
    </div>
  );
};
